/** Canvas ↔ Workspace bidirectional spatial shell transition — shared
 * coordinator/helpers
 * (VISTOERA_WORKSPACE_CANVAS_BIDIRECTIONAL_SPATIAL_SHELL_TRANSITION_AND_VIEWPORT_CONTINUITY_P0_V1).
 *
 * Canonical primitive both directions share:
 * - Canvas → Workspace (Exit, LEFT→RIGHT): `CanvasBrandEntry`'s V logo
 *   calls `useCanvasWorkspaceTransition().requestExitToWorkspace(() =>
 *   navigate(destination))`. The Canvas Surface (`CanvasTransitionSurface`
 *   in `WorkspaceLayout.tsx`) translates off-screen right over
 *   `CANVAS_WORKSPACE_TRANSITION_DURATION_MS`, THEN the real route
 *   handoff fires — spec §11/§14 ("Exit animation → route handoff", never
 *   the reverse).
 * - Workspace → Canvas (Enter, RIGHT→LEFT): every canonical
 *   Workspace-side "open/resume Canvas" call site uses
 *   `openCanvasFromWorkspace(navigate, route)` instead of a bare
 *   `navigate(route)`. It tags the navigation with
 *   `ENTER_FROM_WORKSPACE_STATE` via router `state` (spec §82 — never
 *   used as real business data, just a one-shot transition intent flag);
 *   `CanvasTransitionSurface` reads it ONCE at mount to start translated
 *   off-screen right, then rAF into its resting `translateX(0)` — spec
 *   §16 "FIRST_VISIBLE_CANVAS_FRAME_STARTS_FROM_RIGHT=YES". Direct
 *   reload/deep-link (no router `state`) skips this — spec §34/§35.
 *
 * The actual state MACHINE (guard against rapid clicks, reduced-motion
 * bypass, unconditional recovery back to `idle`) lives in
 * `canvasExitTransitionController.ts` — framework-agnostic on purpose so
 * it is regression-tested head-less. This file is the thin React
 * Context wrapper + the tiny pieces of shared state/helpers that DO need
 * React (`useNavigate`'s `NavigateFunction` type, Context, the hook). */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { NavigateFunction } from 'react-router-dom'
import {
  createCanvasExitTransitionController,
  type CanvasExitTransitionController,
  type CanvasTransitionPhase,
} from './canvasExitTransitionController'
import { freezeCurrentTenantBackofficeLocation } from './tenantBackofficeReturnLocation'

export type { CanvasTransitionPhase } from './canvasExitTransitionController'

/** Spec §45 — "建议 220–320ms。默认优先：约 260ms". */
export const CANVAS_WORKSPACE_TRANSITION_DURATION_MS = 260

/** Spec §47 — fast/precise/spatial, no spring overshoot. Kept as one
 * canonical token so the CSS (`production-canvas.css`) and any future JS
 * timing math never drift apart. */
export const CANVAS_WORKSPACE_TRANSITION_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)'

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

/** Spec §62 — reduced motion shortens the transition to "近乎即时" by
 * skipping the visual phase entirely (0ms — see
 * `createCanvasExitTransitionController`'s `durationMs <= 0` branch). */
export function canvasTransitionDurationMs(): number {
  return prefersReducedMotion() ? 0 : CANVAS_WORKSPACE_TRANSITION_DURATION_MS
}

/** Spec §82 — router `state` transition intent flag. Never persisted,
 * never read as business data — `contentProjectId`/`workspaceId` etc.
 * always come from the URL, same as before this task. */
export const ENTER_FROM_WORKSPACE_STATE = { canvasTransitionIntent: 'ENTER_FROM_WORKSPACE' } as const

export function isEnterFromWorkspaceState(state: unknown): boolean {
  return (
    Boolean(state) &&
    typeof state === 'object' &&
    (state as Record<string, unknown>).canvasTransitionIntent === 'ENTER_FROM_WORKSPACE'
  )
}

/** Canonical Workspace → Canvas entry helper (spec §33/§43 —
 * `openCanvasFromWorkspace`). Every in-app "open/resume Canvas" action
 * (Sider brand mark resume, Content Project list/create, legacy
 * workbench "生产画布" button) should call this instead of a bare
 * `navigate(route)` so the Enter (right→left) transition stays
 * consistent across entry points — never just one button (spec §33/PASS
 * #12).
 *
 * Deliberately NOT used by redirect-stub routes (`WorkspaceHomeEntryPage`,
 * `ContentProductionProjectRedirect`) or Canvas→Canvas project switches
 * (`CanvasTopBar` recent list, project duplicate) — those are either
 * deep-link-shaped resolutions (spec §34/§35) or never leave the Canvas
 * shell in the first place, so neither should play the Enter slide. */
/** Canonical Canvas → Backoffice exit helper (TASK-00048 §4-§9).
 *
 * All Canvas exit triggers (CanvasBrandEntry, Global Account Menu Profile/
 * Workspace/Plan Usage) must funnel through `requestExitToWorkspace` so
 * they share TASK-00032's LEFT→RIGHT slide — never a bare `navigate()`. */
export function exitCanvasToBackoffice(
  requestExitToWorkspace: CanvasWorkspaceTransitionApi['requestExitToWorkspace'],
  navigate: NavigateFunction,
  destinationPath: string,
  options?: { scrollY?: number },
): void {
  requestExitToWorkspace(() =>
    navigate(
      destinationPath,
      options?.scrollY && options.scrollY > 0
        ? { state: { tenantBackofficeReturnScrollY: options.scrollY } }
        : undefined,
    ),
  )
}

export function openCanvasFromWorkspace(navigate: NavigateFunction, route: string): void {
  // VISTOERA_CANVAS_TO_TENANT_BACKOFFICE_RETURN_STATE_RESTORE_SKELETON_
  // FLASH_ELIMINATION_AND_LAST_BACKOFFICE_LOCATION_MEMORY_P0_V1 §26/§43 —
  // this is the single choke point every canonical "open/resume Canvas"
  // call site already funnels through, so it is also the single place
  // that freezes "the last healthy Tenant Backoffice page" before the
  // Canvas route swap unmounts it. No-ops when the CURRENT route isn't
  // actually a Tenant Backoffice route (e.g. already on Canvas — never
  // happens via this helper — or mid-redirect).
  freezeCurrentTenantBackofficeLocation()
  navigate(route, { state: ENTER_FROM_WORKSPACE_STATE })
}

export interface CanvasWorkspaceTransitionApi {
  phase: CanvasTransitionPhase
  requestExitToWorkspace: (onNavigate: () => void) => void
}

const noopApi: CanvasWorkspaceTransitionApi = {
  phase: 'idle',
  requestExitToWorkspace: (onNavigate) => onNavigate(),
}

const CanvasWorkspaceTransitionContext = createContext<CanvasWorkspaceTransitionApi>(noopApi)

/** Consumed by `CanvasBrandEntry` (the ONLY Canvas exit trigger — spec
 * §30) and `CanvasTransitionSurface` (applies the `is-exiting` CSS
 * class). Falls back to a synchronous no-op `noopApi` outside the
 * Provider so any incidental usage never throws. */
export function useCanvasWorkspaceTransition(): CanvasWorkspaceTransitionApi {
  return useContext(CanvasWorkspaceTransitionContext)
}

/** Mounted once per `/workspace/:workspaceId/*` subtree
 * (`WorkspaceLayout.tsx`) — persists across the Canvas ↔ Workspace route
 * swap itself (same nested-route component instance, only the Outlet's
 * matched child + this early-return branch change), which is exactly
 * why a SINGLE controller instance can coordinate "click V while still
 * showing Canvas" → "call the real navigate() ~260ms later" without any
 * cross-component ref plumbing. */
export function CanvasWorkspaceTransitionProvider({ children }: { children: ReactNode }) {
  const controllerRef = useRef<CanvasExitTransitionController | null>(null)
  if (!controllerRef.current) {
    controllerRef.current = createCanvasExitTransitionController(canvasTransitionDurationMs())
  }
  const [phase, setPhase] = useState<CanvasTransitionPhase>(() => controllerRef.current!.getPhase())

  useEffect(() => {
    const controller = controllerRef.current!
    return controller.subscribe(() => setPhase(controller.getPhase()))
  }, [])

  useEffect(() => {
    const controller = controllerRef.current
    return () => controller?.dispose()
  }, [])

  const requestExitToWorkspace = useCallback((onNavigate: () => void) => {
    controllerRef.current?.requestExit(onNavigate)
  }, [])

  const api = useMemo<CanvasWorkspaceTransitionApi>(
    () => ({ phase, requestExitToWorkspace }),
    [phase, requestExitToWorkspace],
  )

  return (
    <CanvasWorkspaceTransitionContext.Provider value={api}>{children}</CanvasWorkspaceTransitionContext.Provider>
  )
}
